import React, {useState, useEffect} from "react"
import {useLocation} from "react-router-dom"
import {useLanguage} from "../../context/LanguageContext"
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from "@mui/material"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import CircularProgress from "@mui/material/CircularProgress"
import "./ListCards.css"
import {Config} from "../../Config/config"
import {sortProductsByOrder} from "../../utils/sortByOrder"

export default function ListCards() {
  const {language} = useLanguage()
  const {state} = useLocation()
  const {subCategoryId, hasOption} = state || {}
  const [products, setProducts] = useState([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [loading, setLoading] = useState(true) // Add loading state

  const languageCode = language || "TR"

  // Fetch products based on subCategoryId
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true) // Start loading

      const response = await fetch(
        `${Config.ApiBaseUrl}/product/?firm=2&is_active=true`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Token ${Config.Token}`,
          },
        }
      )
      const data = await response.json()

      const filteredProducts = data.filter(
        (product) => product.sub_category === subCategoryId
      )
      setProducts(sortProductsByOrder(filteredProducts))
      setLoading(false) // Stop loading when data is fetched
    }
    fetchData()
  }, [subCategoryId])

  const openModal = (product) => {
    setSelectedProduct(product)
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setSelectedProduct(null)
  }

  const getProductLanguageData = (product, languageCode) => {
    const languageData = product.productlanguage.find(
      (lang) => lang.language__code === languageCode
    )
    return (
      languageData ||
      product.productlanguage.find((lang) => lang.language__code === "TR")
    )
  }

  // Get the product price for a specific unit
  const getProductPrice = (product, unitName) => {
    const price = product.productprice.find(
      (price) => price.unit__name === unitName
    )
    return price ? price.price : "Not available"
  }

  // Group products by their unit name
  const groupedByUnit = products.reduce((acc, product) => {
    product.productprice.forEach((price) => {
      const unitName = price.unit__name || "No Unit"
      if (!acc[unitName]) {
        acc[unitName] = []
      }
      acc[unitName].push(product)
    })
    return acc
  }, {})

  return (
    <>
      {/* Loading Spinner */}
      {loading ? (
        <div className="loading-spinner">
          <CircularProgress size={50} color="black" />
        </div>
      ) : (
        <div className={`list-card-container ${hasOption ? "has-option" : ""}`}>
          {hasOption
            ? // Show accordion only when `hasOption` is true
              Object.keys(groupedByUnit).map((unitName) => {
                return (
                  <Accordion key={unitName}>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls={`panel-${unitName}-content`}
                      id={`panel-${unitName}-header`}
                    >
                      <Typography>{unitName}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      {groupedByUnit[unitName].map((product) => {
                        const productData = getProductLanguageData(
                          product,
                          languageCode
                        )
                        const productPrice = getProductPrice(product, unitName)

                        return (
                          <div
                            className="list-card"
                            key={product.id}
                            onClick={() => openModal(product)}
                          >
                            <div className="list-card-image-container">
                              <img
                                className="list-card-image"
                                src={product.image}
                                alt={product.name}
                              />
                            </div>

                            <div className="list-card-info">
                              <h3 className="list-card-name">
                                {productData?.name}
                              </h3>
                              <div className="list-card-price">
                                {productPrice} TL
                              </div>
                              <p className="list-card-description">
                                {productData?.description}
                              </p>
                            </div>
                          </div>
                        )
                      })}
                    </AccordionDetails>
                  </Accordion>
                )
              })
            : // Render products without accordion when `hasOption` is false
              products.map((product) => {
                const productData = getProductLanguageData(
                  product,
                  languageCode
                )
                const productPrice = getProductPrice(
                  product,
                  product.productprice[0]?.unit__name
                )

                return (
                  <div
                    className="list-card"
                    key={product.id}
                    onClick={() => openModal(product)}
                  >
                    <div className="list-card-image-container">
                      <img
                        className="list-card-image"
                        src={product.image}
                        alt={product.name}
                      />
                    </div>

                    <div className="list-card-info">
                      <h3 className="list-card-name">{productData?.name}</h3>
                      <div className="list-card-price">{productPrice} TL</div>
                      <p className="list-card-description">
                        {productData?.description}
                      </p>
                    </div>
                  </div>
                )
              })}
        </div>
      )}

      {/* Modal */}
      {isModalOpen && selectedProduct && (
        <div className="modal" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <span className="close" onClick={closeModal}>
              &times;
            </span>
            <img
              className="modal-image"
              src={selectedProduct.image}
              alt={selectedProduct.name}
            />
            <div className="modal-info">
              {(() => {
                const productData = getProductLanguageData(
                  selectedProduct,
                  languageCode
                )
                return (
                  <>
                    <h3>{productData?.name}</h3>
                    <p>{productData?.description}</p>
                    <div className="modal-price">
                      {getProductPrice(
                        selectedProduct,
                        selectedProduct.productprice[0]?.unit__name
                      )}
                      TL
                    </div>
                  </>
                )
              })()}
            </div>
          </div>
        </div>
      )}
    </>
  )
}
